import Image from "next/image";
import Link from "next/link";
import { isDefined } from "ts-is-present";
import { Box } from "~/components-styled/box";
import { Checklist } from "~/components-styled/checklist";
import { ContentCollapsible } from "~/components-styled/content-collapsible";
import { ContextMenuHorizontal } from "~/components-styled/context-menu";
import { HighlightContent } from "~/components-styled/highlight-content";
import { List } from "~/components-styled/list";
import { PageContent } from "~/components-styled/page-content";
import { PageTitle } from "~/components-styled/page-title";
import { SectionSeparator } from "~/components-styled/section-separator";
import { SeparateChildren } from "~/components-styled/separate-children";
import { Spacer } from "~/components-styled/spacer";
import { Heading, Paragraph, Text } from "~/components-styled/typography";
import { PublicPageLayout, useBaymardStatistics } from "~/domain/app";
import { usePublicNavigationStructure } from "~/domain/app/navigation";
import { BookSession, useBookSession } from "~/domain/audit-page/book-session";
import {
  fetchTestimonials,
  LargeTestimonial,
  TestimonialList,
} from "~/domain/testimonials";
import { createStaticProps, StaticProps } from "~/lib/data/server";

export const getStaticProps = createStaticProps(async () => {
  const testimonials = await fetchTestimonials();
  const listIds = [
    134, 177, 193, 116, 137, 138, 186, 117, 190, 188, 164, 182, 169, 17, 142,
    198, 130, 185, 115, 124, 197, 160, 176, 98, 131, 178, 195, 128, 155, 157,
    135, 133, 180, 146, 145, 20, 136, 97, 26, 2, 118, 132, 126, 103, 22, 122,
    121, 23, 95, 16, 15, 166, 153, 56, 165, 173, 143, 158, 199, 162, 156, 187,
    189, 200, 192,
  ];

  return {
    props: {
      testimonials: {
        list: listIds
          .map((id) => testimonials.find((x) => x.id === id))
          .filter(isDefined),
        nike: testimonials.find((x) => x.id === 134),
        oura: testimonials.find((x) => x.id === 193),
        columbia: testimonials.find((x) => x.id === 138),
        google: testimonials.find((x) => x.id === 177),
      },
    },
  };
});

function AuditContextMenu() {
  const bookSession = useBookSession("dtc");

  return (
    <ContextMenuHorizontal
      items={[
        {
          type: "title",
          title: "Direct-To-Consumer",
          category: "UX Audit",
          href: "#top",
        },
        { title: "What’s Included", href: "#features" },
        { title: "Focus Areas", href: "#audit-focus" },
        { title: "Pricing", href: "#pricing" },
        { title: "Testimonials", href: "#testimonials" },
        { type: "cta", title: "Schedule a Call", onClick: bookSession },
      ]}
    />
  );
}

export default function AuditPage({
  testimonials,
}: StaticProps<typeof getStaticProps>) {
  const stats = useBaymardStatistics();
  const structure = usePublicNavigationStructure();

  return (
    <PublicPageLayout
      id="top"
      pageId="audit-dtc"
      seoTitle="Direct-To-Consumer (DTC) E-Commerce UX Audit"
      seoDescription={`Based on ${stats.research.researchHoursRounded} hours of User Experience (UX) research, Baymard Institute will conduct a UX audit of your Direct-To-Consumer (DTC) site, comparing it against other large or niche DTC sites.`}
      analyticsCategory="Audit Sales Page: DTC"
      stickyBarElement={<AuditContextMenu />}
      replaceBreadcrumbTrail={() => []}
    >
      <PageTitle
        title="Direct-To-Consumer E-Commerce UX Audit"
        category={structure.audit_service.label}
        categoryLink={structure.audit_service.url}
        introduction="What are the top 40 UX improvements for your Direct-To-Consumer website? How does your UX performance compare to other large or niche DTC sites?"
        image={require("~/assets/audit-dtc-header.jpg")}
      />
      <PageContent text="editorialBody">
        <p>
          Based on the findings from Baymard Institute’s{" "}
          {stats.research.researchHoursRounded} hours of User Experience (UX)
          research, Baymard will conduct a{" "}
          <strong>
            UX audit of your Direct-To-Consumer (DTC) e-commerce website
          </strong>
          .
        </p>
        <p>
          The in-depth DTC UX audit will be based on 400+ weighted UX parameters
          uncovered during our large-scale UX testing of Direct-To-Consumer
          websites. The DTC UX audit will provide you with:
        </p>
        <List>
          <li>
            a detailed 120+ page audit report outlining up to 40 prioritized and
            research-backed <strong>user experience improvements</strong> for
            your DTC site,
          </li>
          <li>
            provide best practice implementation examples and inspiration from
            other DTC sites for <em>each</em> of the up to 40 identified UX
            improvements to your site,
          </li>
          <li>
            a detailed, unbiased UX performance analysis across the 400+ UX
            parameters to document your current state and identify the best
            areas to improve,
          </li>
          <li>
            compare your site’s UX performance directly to the performance of 36
            other DTC e-commerce sites, either large or niche DTC brands, and
            within industries such as apparel, consumer electronics, home decor,
            B2B, beauty, or luxury goods. (
            <em>
              Adidas, L.L. Bean, American Eagle, Marks &amp; Spencer, Nike,
              H&amp;M, GAP, Victoria’s Secret, Ann Taylor, Gucci, Van Cleef
              &amp; Arpels, Louis Vuitton, Jimmy Choo, Chanel, TAG Heuer, Apple,
              Bang &amp; Olufsen, HP, Dell, IKEA, Disney Store, Thermo Fisher,
              Hitachi, along with Fromourplace.com, Snowehome.com,
              Backdrophome.com, Awaytravel.com, Everlane.com, Aninebing.com,
              Fitbit.com, Gopro.com, Harrys.com, Smiletwice.com
            </em>
            ).
          </li>
        </List>
        <p>
          Learn more about our Direct-to-Consumer UX audit analysis,
          deliverables, and costs in the 5 sections below:
        </p>
      </PageContent>
      <SectionSeparator />
      {testimonials.nike && (
        <PageContent>
          <LargeTestimonial testimonial={testimonials.nike} />
        </PageContent>
      )}
      <SectionSeparator />
      <PageContent text="editorialBody" id="features">
        <Heading level={2}>What’s Included in a DTC UX Audit?</Heading>
        <p>A ‘Full-Site DTC UX audit’ includes:</p>
        <Checklist>
          <li>
            <p>
              A full <strong>UX analysis and assessment</strong> of your desktop
              and mobile websites UX, including the homepage, category
              navigation, on-site search, product listing pages, product details
              pages, shopping cart, and checkout process — conducted by lead
              researchers at Baymard Institute, utilizing our{" "}
              {stats.research.researchHoursRounded} hours of large-scale UX
              research.
            </p>
          </li>
          <li>
            <p>
              7 detailed UX scorecards with 400+ UX performance scoring
              parameters, used for direct{" "}
              <strong>UX performance comparison</strong> against 36 DTC
              websites. In addition, you will also get a UX comparison to{" "}
              {stats.benchmark.caseStudiesCount} top-grossing US and European
              e-commerce sites, to benchmark how your site stacks up to general
              user expectations.
            </p>
          </li>
          <li>
            <p>
              The auditors will write a{" "}
              <strong>detailed 120+ page report</strong> with 40 suggestions for
              UX improvement. Each suggestion includes a description of the
              identified user experience issue along with a proposed solution
              and 2–4 best practice examples from other DTC sites.
            </p>
          </li>
          <li>
            <p>
              A <strong>2-hour video conference</strong> where we go over the
              audit results with you and your team, to discuss the findings and
              suggestions for improvements.
            </p>
          </li>
          <li>
            <p>
              A full-site UX audit includes 4 months of complimentary access to
              a{" "}
              <Link href="/research" passHref>
                <Text interaction="anchor" as="a">
                  Baymard Premium
                </Text>
              </Link>{" "}
              ‘Medium’ plan with full access to Baymard’s{" "}
              {stats.research.researchHoursRounded} hours of UX research
              findings.
            </p>
          </li>
        </Checklist>
      </PageContent>

      <SectionSeparator />

      {testimonials.oura && (
        <PageContent>
          <LargeTestimonial testimonial={testimonials.oura} />
        </PageContent>
      )}

      <SectionSeparator />

      <PageContent text="editorialBody" id="audit-focus">
        <Heading level={2}>DTC Audit Focus</Heading>
        <Paragraph>
          The audit will cover the following areas of your online user
          experience:
        </Paragraph>

        <Spacer css={{ my: "$3" }} />

        <SeparateChildren>
          <ContentCollapsible
            title="Homepage &amp; Category Navigation"
            description="Design and user experience of your homepage, the main navigation from a DTC perspective, the overall site taxonomy, ‘about the brand’ pages, general site-wide navigation, etc."
          >
            <List>
              <li>
                <p>
                  <strong>Homepage —</strong> The homepage structure, design,
                  carousels, personalization, and promotions.
                </p>
              </li>
              <li>
                <p>
                  <strong>Category Taxonomy —</strong> The overall e-commerce
                  category structure and taxonomy, includes the common issue of
                  “over-categorization”, information architecture, catalog
                  breadth, category redundancy, overly deep or shallow
                  categories, industry-specific naming, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>DTC Main Navigation —</strong> The main navigation
                  design from a DTC perspective, product navigation and its
                  visual hierarchy, courtesy non-product navigation, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Site-Wide Layout —</strong> Site-wide layouts,
                  footers, newsletter dialogs, ad positioning, and return policy
                  links, etc.
                </p>
              </li>
            </List>
          </ContentCollapsible>

          <ContentCollapsible
            title="On-Site Search Experience"
            description="(If search is applicable, typically only on DTC sites with a larger product catalog). The type of search queries the search engine supports, the logic for your autocomplete suggestions, how users are guided in case of partial matches, “No results” pages, results layout, etc."
          >
            <List>
              <li>
                <p>
                  <strong>Query Types —</strong> The level of compliance with 12
                  different overarching types of search queries that users make
                  on e-commerce sites, all vital for your search engine to
                  support (Exact, Product Type, Feature, Thematic, Relational,
                  Compatibility, Symbol, Subjective, Symptom, Implicit,
                  Non-Product, and Natural Language). How and what product data
                  is actually searched.
                </p>
              </li>
              <li>
                <p>
                  <strong>Search Form &amp; Logic —</strong> The design and
                  behavior of the search field itself, query persistence, manual
                  and automatic search scope selection, special characters, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Autocomplete —</strong> How the autocomplete feature
                  (“predictive search”) should be designed, the types of
                  suggestions it should make, keyboard behavior support,
                  suggesting scopes and products, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Results Logic &amp; Guidance —</strong> How to guide
                  users toward better paths on the results page, including
                  suggesting alternate queries, scopes, “No results” pages,
                  misspellings, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Results Layout —</strong> The layout and features of
                  the search results page, such as dynamic list layouts, the
                  search results information, search snippets, pagination, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Results Filtering &amp; Sorting —</strong> Filtering
                  and sorting of search results, including essential filtering
                  types, faceted search, the design and logic for long lists of
                  filters, filter naming, dynamic sorting, etc.
                </p>
              </li>
            </List>
          </ContentCollapsible>

          <ContentCollapsible
            title="Product Lists &amp; Filtering"
            description="Design and usability of your category and search product listing pages, design and logic for all filtering and sorting tools, the list item design and information, etc."
          >
            <List>
              <li>
                <p>
                  <strong>List Layout —</strong> The design and features of the
                  overall layout of the product list, includes “list” vs. “grid”
                  layouts, product data consistency, post-processing
                  vendor-supplied data, “responsive upscaling” of product lists,
                  etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Loading Products —</strong> The loading schema for new
                  items loaded into the product list, number of products
                  displayed by default, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>List Items: What Information to Include —</strong> The
                  type and amount of product attributes to include in each list
                  item, product variations in list items, user ratings averages
                  in list items, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>List Items: User Interface —</strong> The styling and
                  display of the included information such a price, product
                  specifications, variations, units, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>List Items: Product Thumbnails —</strong> The type of
                  product thumbnails in each list item, what they depict, their
                  size, hover-enabled thumbnails, showing packaging, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>List Items: Hover &amp; Hit Areas —</strong> The list
                  item’s click behavior, hover effect synchronization, “quick
                  view” features, dynamic amount of content on hover, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>List Items: Personalization —</strong> Context-aware
                  thumbnails, personalized list item and product attributes,
                  highlighting of items, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Filtering: Available Filters —</strong> The type,
                  amount, and specificity of filters users need, incl. thematic
                  filters, product-specific filters, compatibility filters,
                  symptom filters, product status filters, gifting filters,
                  user-defined filters, price filters, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Filtering: Scope &amp; Logic —</strong> Filtering
                  logic, scope, and naming schemas, such as over-categorization,
                  mutually exclusive filtering values and non-exclusive values
                  (“AND” “OR” logics), filtering tooltips, filter names, filter
                  value consistency, dynamically renaming filters, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Filtering Interface &amp; Layout —</strong> Filtering
                  type’s and filtering value’s styling, placement, filter
                  truncation, incl. handling filtering values with 0 matches,
                  filter value checkbox and link styling, the display sequence
                  for filtering values, slider interfaces, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Filtering: Applying &amp; Applied Filters —</strong>{" "}
                  “Apply” buttons and live updating as filters are applied, the
                  position and styling for applied filtering values, browser
                  history, filter and sorting persistence, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Sorting —</strong> The default sort type, diversity in
                  sorting logic, the sorting interface and scope, alphabetical
                  sorting, category-specific sort types, sorting directions,
                  sorting naming, the scope of the sorting tool, labeling of the
                  sorting interface, dead-end sorting options, logic for
                  customer rating sorting, etc.
                </p>
              </li>
            </List>
          </ContentCollapsible>

          <ContentCollapsible
            title="Product Details Pages"
            description="The design and usability of your entire product page implementation, including product images and videos, the image gallery UI, page features, “Add to Cart”, etc."
          >
            <List>
              <li>
                <p>
                  <strong>Product Page Layouts —</strong> “Horizontal Tabs”,
                  “Sticky TOCs”, “Collapsed Sections”, “One Long Page”, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Product Images —</strong> How users inspect product
                  images, the 7 different image types e-commerce sites need,
                  number of images, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Image Gallery User Interface —</strong> Image gallery
                  design, image navigation, methods of zoom, carousels,
                  overlays, default image and size, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>The “Buy” Section —</strong> Design and position of
                  elements such as “Add to Cart” button, prices and discounts,
                  quantity field, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Shipping, Returns, &amp; Gifting —</strong> Shipping
                  and returns information at the product page, “Free Shipping”
                  tiers, and “Find in Store” features.
                </p>
              </li>
              <li>
                <p>
                  <strong>Product Variations —</strong> Selecting color and size
                  variations, content across variations, product customizations
                  and personalizations.
                </p>
              </li>
              <li>
                <p>
                  <strong>Product Description —</strong> Product information and
                  descriptions, the type of content needed, text styling,
                  sub-titles, product headlines, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Product Specifications Sheet —</strong> Spec sheet
                  layout, specs in tables vs. product descriptions, help
                  features, post-processing of vendor data, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>User Reviews —</strong> (If applicable) User reviews,
                  the review submission form, review filtering, sorting and
                  navigation, rating distribution summaries, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Auxiliary Content —</strong> Q&amp;A/FAQs, social
                  media tools, and expert-generated content.
                </p>
              </li>
              <li>
                <p>
                  <strong>Cross-Sells &amp; Cross-Navigation —</strong>{" "}
                  Cross-sells design, placement, and logic, cross-navigation
                  elements, “Recently Viewed Products”, etc.
                </p>
              </li>
            </List>
          </ContentCollapsible>

          <ContentCollapsible
            title="The Checkout Process"
            description="The user experience of your entire checkout flow, including shopping cart, account selection, guest checkout, detailed form field usability, field labels, security concerns, shipping methods, payment methods and fields, wording of error messages, etc."
          >
            <List>
              <li>
                <p>
                  <strong>
                    Shopping Cart &amp; “Added to Cart” Behavior —
                  </strong>{" "}
                  Shopping cart design, quantity, and save features, as well as
                  the page-response when users add products to the cart
                  (drop-down cart, etc.)
                </p>
              </li>
              <li>
                <p>
                  <strong>Account Selection &amp; Creation —</strong> Account
                  selection designs and communication, guest checkout, “Delayed
                  Account Creation”, social media accounts, password rules, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Customer &amp; Address Information —</strong> Privacy
                  concerns, shipping addresses, billing addresses, international
                  addresses, phone fields, address auto-detection techniques,
                  etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Shipping &amp; Store Pickup —</strong> Shipping
                  interface design and information hierarchy, shipping
                  descriptions, order cut-off times, and “Free Shipping” tiers,
                  along with omni-channel features such as Store Pickup, Store
                  Availability, and Ship to Store.
                </p>
              </li>
              <li>
                <p>
                  <strong>
                    Payment Flow &amp; Methods (includes Third-Party) —
                  </strong>{" "}
                  The payment methods interface, how to integrate and display
                  third-party payment options, gift card redemption flows,
                  international currencies, coupon codes, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Credit Card Form —</strong> The credit card field
                  design and its validation logic and formatting, along with the
                  expiration date, security code, and cardholder name inputs,
                  field sequence, card icons, and card type selection.
                </p>
              </li>
              <li>
                <p>
                  <strong>Order Review —</strong> Review step design, “Place
                  Order” button placement, and necessary review data, along with
                  its editing flow.
                </p>
              </li>
              <li>
                <p>
                  <strong>
                    Order Confirmation &amp; E-Mail (to the extent it’s possible
                    to access this) —
                  </strong>{" "}
                  The information and actions needed on the order confirmation
                  page, along with the order confirmation e-mail.
                </p>
              </li>
              <li>
                <p>
                  <strong>Checkout Page Design —</strong> Checkout process
                  steps, “Enclosed Checkout” designs, order summary sidebars,
                  and how to design and position the cart link in the site-wide
                  header.
                </p>
              </li>
              <li>
                <p>
                  <strong>User Attention &amp; Interactions —</strong> Load
                  indicators, feedback on user actions, the use of overlays,
                  feedback requests, introduction of site-specific features, and
                  embedded content from third-parties.
                </p>
              </li>
              <li>
                <p>
                  <strong>Cross-Sells —</strong> Active vs. passive
                  cross-selling, adapting cross-sells to user context, and
                  pitfalls of specific placements and wording.
                </p>
              </li>
              <li>
                <p>
                  <strong>Form Design &amp; Features —</strong> How to design
                  and position the primary button, “Apply” buttons, minimizing
                  form intimidation, multi-column layouts, tabbing flows,
                  CAPTCHAs, back-button and enter-keystroke behavior.
                </p>
              </li>
              <li>
                <p>
                  <strong>Validation Errors &amp; Data Persistence —</strong>{" "}
                  How to design and position error messages, how to word them,
                  and persisting data in non-secure and secure form fields,
                  along with inline validation.
                </p>
              </li>
              <li>
                <p>
                  <strong>Address Validators —</strong> When address validations
                  are necessary, “Force-Proceeding” through address validators,
                  along with address validator design and logic.
                </p>
              </li>
              <li>
                <p>
                  <strong>Field Labels &amp; Microcopy —</strong> Marking
                  optional and required fields, field label position, tooltips,
                  and optimizing microcopy and descriptions.
                </p>
              </li>
              <li>
                <p>
                  <strong>Field Design &amp; Features —</strong> How to match
                  the type of interface with the input type, drop-downs and
                  radio button implementations, input masks and character
                  restrictions, custom designed input fields, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Default Values &amp; Autocompletion —</strong>{" "}
                  Intelligent form features, including inferred selections,
                  pre-fills, auto-fills, and personalization of the checkout
                  flow.
                </p>
              </li>
            </List>
          </ContentCollapsible>

          <ContentCollapsible
            title="Customer Account"
            description="The design and usability of the ‘My Account’ section (if applicable), such as ‘My Account’ navigation and dashboard, sign-in and password reset flows, updating stored credit card and address info, newsletter frequency and unsubscribe, etc."
          >
            <List>
              <li>
                <p>
                  <strong>‘My Account’ Sign-In —</strong> Account sign in,
                  including password reset and account lockouts, “Soft” sign in,
                  automatic sign out, and where users should be sent after
                  signing in.
                </p>
              </li>
              <li>
                <p>
                  <strong>‘My Account’ Drop-Down —</strong> Placement of the “My
                  Account” drop-down menu, account features to include, how the
                  “My Account” drop-down should be structured and styled,
                  personalization, and what “My Account” drop-down
                  implementations should be avoided.
                </p>
              </li>
              <li>
                <p>
                  <strong>‘My Account’ Dashboard —</strong> Providing paths to
                  all ‘My Account’ features, highlighting recent orders,
                  limiting ads, and using icons; dashboard designs that use a
                  sidebar or “Cards “for navigation.
                </p>
              </li>
              <li>
                <p>
                  <strong>
                    ‘My Account’ Stored Addresses, Cards, and Newsletters —
                  </strong>{" "}
                  Credit card updating flow, default addresses, editing vs.
                  adding a new address, newsletter frequency and “unsubscribe “,
                  confirmations, and ‘Apply’ buttons.
                </p>
              </li>
            </List>
          </ContentCollapsible>

          <ContentCollapsible
            title="Mobile Specific"
            description="In addition to auditing all of the above site elements on your mobile website, Baymard will also audit for mobile-specific issues, such as how the interface handles users’ lack of page overview, touch interactions and hit areas for the main menu, mobile image zoom, mobile search, mobile touch keyboards, design deviations between mobile and desktop, etc."
          >
            <List>
              <li>
                <p>
                  <strong>Mobile Basics —</strong> Content and structure of
                  mobile site vs. desktop site, size and spacing of hit areas,
                  multiple hit-areas within the same visual element, font-sizes,
                  landscape mode, bugs and quirks vulnerable to the mobile
                  platform, auto-zoom, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Mobile Site Homepage &amp; Main Navigation —</strong>{" "}
                  Main navigation design and logic, double-hit area issues,
                  homepage structure and design, auto-rotating carousels, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Mobile Search —</strong> User’s crossover from
                  category navigation to search, search scopes, misspellings and
                  synonyms, search query support, “No results” pages, Faceted
                  Search filters, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Mobile Product Lists —</strong> List item size,
                  product thumbnail size relative to product type, amount of
                  product attributes in list items, hit areas within the product
                  list, separation of list items, pagination vs. load more vs.
                  endless scrolling, visual indicators, product variations, list
                  item interactions on a touch screen, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Mobile Filtering &amp; Sorting —</strong> Styling and
                  position of both “filter” and “Sort” options, sort types,
                  “apply” buttons vs. auto-applying filtering values, the
                  display of applied filters, information scent, product status,
                  filtering interface, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Mobile Cross-Navigation &amp; Compatibility —</strong>{" "}
                  Interlinking of compatibility-dependent products, cross-sells,
                  list item consistency, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Mobile Product Page Layouts —</strong> Product page
                  structure, sub–product pages, collapsed product page sections,
                  user reviews, “add to cart” button placement, “Store Pickup”,
                  etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Mobile Product Images —</strong> Product image size,
                  product image zoom levels, image ambiguity, touch gestures in
                  the image gallery, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>
                    Mobile Product Descriptions, Specs, &amp; Compatibility —
                  </strong>{" "}
                  Division of product info between description and spec list,
                  styling of product description, amount and level of specs
                  included, product compatibility information and relationships,
                  etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Mobile Shopping Cart —</strong> Cost estimates, number
                  and placement of “checkout” buttons, saving mobile carts,
                  picking up mobile carts on desktop devices, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Mobile Checkout Steps —</strong> Account creation and
                  guest checkout, shipping and billing addresses, shipping
                  methods, “Store Pickup”, payment, order review, linear
                  checkout flows, mobile process steps, etc.
                </p>
              </li>
              <li>
                <p>
                  <strong>Mobile Checkout UX —</strong> Optional vs. required
                  fields, minimizing number of fields, form field label
                  placement in smartphone portrait and landscape modes, inline
                  labels, floating labels, grouping checkout info for mobile
                  overview, field context, location detection, touch keyboard
                  auto-correct, optimized keyboards, auto-capitalization, etc.
                </p>
              </li>
            </List>
          </ContentCollapsible>
        </SeparateChildren>

        <Spacer css={{ mb: "$4" }} />

        <HighlightContent>
          <Paragraph as="h3" variant="subtitle1">
            Custom DTC Competitors
          </Paragraph>
          <p>
            In addition to auditing your site UX and comparing it to 36 DTC
            e-commerce sites in our benchmark database (
            <em>
              Adidas, L.L. Bean, American Eagle, Marks &amp; Spencer, Nike,
              H&amp;M, GAP, Victoria’s Secret, Ann Taylor, Gucci, Van Cleef
              &amp; Arpels, Louis Vuitton, Jimmy Choo, Chanel, TAG Heuer, Apple,
              Bang &amp; Olufsen, HP, Dell, IKEA, Disney Store, Thermo Fisher,
              Hitachi, along with Fromourplace.com, Snowehome.com,
              Backdrophome.com, Awaytravel.com, Everlane.com, Aninebing.com,
              Fitbit.com, Gopro.com, Harrys.com, Smiletwice.com
            </em>
            ), we can also expand the project scope to include a custom
            competitive audit against any local or direct DTC competitor(s)
            you’d like. This is available for sites in most countries and
            languages.
          </p>
        </HighlightContent>
      </PageContent>

      <SectionSeparator />

      {testimonials.columbia && (
        <PageContent>
          <LargeTestimonial testimonial={testimonials.columbia} />
        </PageContent>
      )}

      <SectionSeparator />

      <PageContent text="editorialBody">
        <Heading level={2}>What Are Audits For?</Heading>
        <Paragraph>Most commonly, UX audit services are used by:</Paragraph>
        <List>
          <li>
            Sites that would like to <strong>benchmark</strong> their UX
            performance to see how they stack up against major competitors and
            “State of the Art” sites, to know where resources are best spent on
            further UX improvements.
          </li>
          <li>
            Sites that would like inspiration and verification of their new{" "}
            <strong>redesign</strong>. It can even be before spending resources
            on the final design and code — auditing prototype designs is cheaper
            than re-coding.
          </li>
          <li>
            Optimized sites that need a pair of{" "}
            <strong>external and unbiased</strong> eyes to identify the last
            tweaks and improvements.
          </li>
          <li>
            Agencies, designers, and tech vendors that want{" "}
            <strong>reassurance</strong> that they’re on the right track with
            their client’s design.
          </li>
        </List>

        <Spacer css={{ mb: "$4" }} />

        <HighlightContent>
          <Paragraph as="h3" variant="subtitle1">
            Return on Investment
          </Paragraph>
          <Paragraph>
            Even the smallest improvement in conversion rate for any site with
            significant sales will lead to a notable return on investment. For
            example, an improvement from 3.1% to 3.6% in conversion rate for a
            site with $50,000,000 annual sales will yield $8,060,000 extra sales
            every year.
          </Paragraph>
        </HighlightContent>
      </PageContent>

      <SectionSeparator />

      {testimonials.google && (
        <PageContent>
          <LargeTestimonial testimonial={testimonials.google} />
        </PageContent>
      )}

      <SectionSeparator />

      <Box id="pricing" spacing="inherit">
        <PageContent text="editorialBody">
          <Heading level={2}>
            How Will the Audit Be Delivered?
            <br />
            What Will It Cost?
          </Heading>
          <Paragraph>
            A full-site Direct-To-Consumer desktop and mobile web UX audit
            across all 7 audit focus areas cost $12,700 (USD) — this includes a
            volume discount of $5,200 compared to the rate for 7 individual
            “focused UX audits”.
          </Paragraph>
        </PageContent>
        <Box css={{ maxWidth: "45rem", m: "0 auto" }}>
          <Image
            alt=""
            src={require("~/assets/audit-example-screenshots.jpg")}
            placeholder="blur"
          />
        </Box>

        <PageContent text="editorialBody">
          <Paragraph>A ‘DTC Full-Site UX audit’ includes:</Paragraph>
          <Checklist>
            <li>
              A <strong>full UX review and analysis</strong> by Baymard’s team
              of UX researchers of both your desktop and mobile websites.
            </li>
            <li>
              A <strong>120+ page audit report</strong> with up to 40
              prioritized suggestions for UX improvements. Each explain the
              identified UX issue, the suggested change, and show 2–4 best
              practice implementation examples from other DTC sites and leading
              e-commerce sites.
            </li>
            <li>
              6 detailed <strong>UX performance scorecards</strong> with 400+
              review parameters, and with performance comparison against 36
              leading DTC sites, along with a general comparison to{" "}
              {stats.benchmark.caseStudiesCount} top-grossing US, European, and
              International e-commerce sites.
            </li>
            <li>
              A <strong>2-hour video conference</strong> with you and your team
              to discuss the audit results.
            </li>
            <li>
              3 <strong>follow-up calls</strong> with your UX auditor. Useful
              for follow-up questions or feedback on your redesigns or
              prototypes.
            </li>
            <li>
              4 months of complimentary access to a{" "}
              <Link href="/research" passHref>
                <Text interaction="anchor" as="a">
                  Baymard Premium
                </Text>
              </Link>{" "}
              ‘Medium’ plan with full access to Baymard’s{" "}
              {stats.research.researchHoursRounded} hours of UX research
              findings.
            </li>
          </Checklist>

          <p>
            Reach out below to discuss your needs or request a UX audit of your
            DTC site. (PS client audits are strictly confidential and will{" "}
            <em>not</em> be included in Baymard’s public UX benchmark database).
          </p>

          <Spacer />

          <Box css={{ "@md": { mx: "-$4" } }}>
            <BookSession auditId="dtc" />
          </Box>
        </PageContent>

        <PageContent css={{ color: "$grayHeavy", text: "body1" }}>
          <List>
            <li>
              <p>
                <strong>Refund Policy:</strong> In the event that we’re unable
                to provide 30 suggestions for improvements we’ll still complete
                the audit and leave it up to you if you want a refund.
              </p>
            </li>
            <li>
              <p>
                A typical full site audit takes around 17-25 work days -
                depending on time of year, project scope and auditor
                availability.
              </p>
            </li>
            <li>
              <p>
                All client-specific audits and UX scorecards are strictly
                confidential; they are not included in Baymard’s public
                benchmark databases or shared with anyone else (NDA is
                possible).
              </p>
            </li>
            <li>
              <p>
                As Baymard does not offer any kind of web design or programming
                services or sell e-commerce technology, the UX audit will always
                be a completely unbiased analysis of your site, and not an
                indirect “sales pitch” for other services. At Baymard our
                specialization is the UX audit.
              </p>
            </li>
          </List>
        </PageContent>
      </Box>

      <Box spacing="inherit" id="testimonials">
        <Heading level={2} css={{ textAlign: "center" }}>
          What Our Clients Say About the UX Audit
        </Heading>

        <TestimonialList testimonials={testimonials.list} limit={12} />
      </Box>
    </PublicPageLayout>
  );
}
